/**
 * The work sheet's ledger (#1717; the v6 prototype's `ledUI`): City filing,
 * Customer, Vendors, Money, Files. Rows, not cards. Amounts right-aligned,
 * status as a word, every action a quiet link.
 *
 * Every number here is a read the page already makes (the job payload, the
 * report lists, send history, the PO and parts reads, the budget chip) or a
 * promise the Finish Line engine already computed. The cards these replace
 * stay one click away in a drawer, so nothing they could do is lost.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AttachmentPreviewSheet } from "@/components/job-reports-list";
import { ReportInputFlag } from "@/components/report-input-flag";
import { SentEmailDialog } from "@/components/sent-email-dialog";
import { WaterReportingEditorDialog } from "@/components/water-reporting-editor";
import { openPriceCheck } from "@/components/price-check";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { api, ApiError, downloadAttachmentFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { openExternalLink } from "@/lib/standalone";
import { formatDate, formatDateTime } from "@/lib/utils";
import type { FinishLine, JobPromise } from "@/lib/finish-line";
import type { SendHistory } from "@/lib/use-send-history";
import { deriveMoneyTotals } from "@/lib/job-story";
import { chipSummary, workCategory } from "@/pages/customers/budget/model";
import { useCustomerBudgets } from "@/pages/customers/budget/chip";
import { RenameFileDialog } from "../rename-file-dialog";
import { isRenameableCategory, provenanceLine } from "../paperwork-provenance";
import { usePacketStatus, useUseAsWorkOrder } from "../use-as-work-order";
import type { JobDetail, LinkedEstimate, LinkedInvoice, ReportAttachment } from "../types";
import { deliveryWord, invoiceWord, money, officeFiles, type OfficeFile } from "./calmModel";
import { LedgerRow, OneLine, Quiet, QuietLink, Section, TONE_INK, Word } from "./calm-ui";
import { Download, Eye, X } from "lucide-react";
import { fileRowActions } from "../jobActions";
import { RowContextMenu, RowIconButton, RowMoreMenu, RowTools } from "./row-tools";

const promise = (fl: FinishLine | null, id: string): JobPromise | undefined => fl?.promises.find((p) => p.id === id);

const PROMISE_TONE: Record<JobPromise["state"], keyof typeof TONE_INK> = {
    kept: "pass",
    open: "warn",
    missed: "warn",
    blocked: "fail",
    waiting: "todo",
    not_applicable: "todo",
    later: "todo",
};

const PROMISE_WORD: Record<JobPromise["state"], string> = {
    kept: "Done",
    open: "On us",
    missed: "Late",
    blocked: "Blocked",
    waiting: "Waiting",
    not_applicable: "",
    later: "After the visit",
};

// ── City filing ─────────────────────────────────────────────────────────────

interface PermitStateLite {
    applies: boolean;
    unanswered: unknown[];
}

export function CityFilingSection({
    job,
    finishLine,
    summaryReports,
    reportCount,
    onOpenPermit,
    editNonce,
}: {
    job: JobDetail;
    finishLine: FinishLine | null;
    summaryReports: ReportAttachment[];
    /** Generated + official per-device reports on the job. */
    reportCount: number;
    onOpenPermit: () => void;
    /** Bump to open the water-authority editor (a job action asked for it). */
    editNonce?: number;
}) {
    const { can } = useAuth();
    const [editing, setEditing] = useState(false);
    useEffect(() => {
        if (editNonce && job.site_id) setEditing(true);
    }, [editNonce, job.site_id]);
    const [preview, setPreview] = useState<ReportAttachment | null>(null);
    // Same query (and key) as the Permit card, so it is one request.
    const permitQ = useQuery({
        queryKey: ["job", job.id, "permit"],
        queryFn: () => api.get<{ permit: PermitStateLite }>(`/api/jobs/${job.id}/permit`),
    });
    const permit = permitQ.data?.permit;
    // Job page v2 (slice 5): registration warnings shown always, not only
    // inside the permit card. Same query (and key) as RegistrationWarningsCard.
    const regQ = useQuery({
        queryKey: ["job-registration-warnings", job.id],
        queryFn: () =>
            api.get<{ providerId: string | null; city: string | null; warnings: Array<{ text: string }> }>(
                `/api/jobs/${job.id}/registration-warnings`,
            ),
        staleTime: 60_000,
        retry: false,
        enabled: can(PERMISSIONS.officeReview.view),
    });
    const regWarnings = regQ.data?.warnings ?? [];
    const filing = promise(finishLine, "filing");
    const editor =
        editing && job.site_id ? (
            <WaterReportingEditorDialog
                siteId={job.site_id}
                customerId={job.customer_id}
                onClose={() => setEditing(false)}
            />
        ) : null;

    if (!job.water_provider_name) {
        if (job.trade_code !== "backflow") return null;
        return (
            <>
                <OneLine
                    id="calm-city"
                    title="City filing"
                    words="no water authority set"
                    actions={job.site_id ? <Quiet onClick={() => setEditing(true)}>Set one</Quiet> : undefined}
                />
                {editor}
            </>
        );
    }

    const authority = job.water_provider_display_name || job.water_provider_name;
    const platform = job.water_provider_submission_platform
        ? job.water_provider_submission_platform
        : job.water_provider_is_portal
          ? "Online portal"
          : "Paper / mail";
    const acct = job.water_provider_is_portal
        ? job.site_water_account_number?.trim()
            ? `Acct ${job.site_water_account_number.trim()}`
            : "no account # yet"
        : null;
    const cheat = summaryReports[0] ?? null;
    // The classic Water & Reporting card's method sentence and legacy ST code
    // (shown only for a portal city with no portal account, as there).
    const method = job.water_provider_is_portal
        ? "Results are entered in the purveyor's online portal"
        : job.water_provider_submission_platform
          ? `Reports are filed via ${job.water_provider_submission_platform}`
          : "Reports are filed on paper or mailed";
    const legacyCode =
        job.water_provider_is_portal && !job.site_water_account_number?.trim()
            ? job.site_account_number?.trim() || null
            : null;

    return (
        <Section
            id="calm-city"
            title="City filing"
            summary={
                <>
                    {authority} · {platform}
                    {acct && (
                        <>
                            {" · "}
                            <span className={acct.startsWith("no") ? TONE_INK.warn : "font-mono text-foreground/80"}>
                                {acct}
                            </span>
                        </>
                    )}
                </>
            }
            actions={
                <>
                    {job.water_provider_id && can(PERMISSIONS.pricing.view) && (
                        <Quiet
                            tone="mute"
                            onClick={() =>
                                openPriceCheck({
                                    providerId: job.water_provider_id!,
                                    providerName: authority || undefined,
                                })
                            }
                        >
                            Price check
                        </Quiet>
                    )}
                    {job.site_id && (
                        <Quiet
                            tone="mute"
                            onClick={() => setEditing(true)}
                            title="Jurisdiction, portal account and on-site contact (saved on the site)"
                        >
                            Edit
                        </Quiet>
                    )}
                    {job.water_provider_portal_url && (
                        <Quiet onClick={() => openExternalLink(job.water_provider_portal_url!)}>Open portal ↗</Quiet>
                    )}
                </>
            }
        >
            <LedgerRow
                testId="calm-city-reports"
                what={`Test reports${reportCount > 0 ? ` · ${reportCount} on the job` : ""}`}
                sub={
                    filing && filing.state !== "not_applicable"
                        ? filing.detail
                        : job.water_provider_report_name
                          ? `Form: ${job.water_provider_report_name}`
                          : undefined
                }
                state={
                    filing && filing.state !== "not_applicable" ? (
                        <Word tone={PROMISE_TONE[filing.state]}>
                            {filing.state === "kept" ? "Filed" : PROMISE_WORD[filing.state]}
                        </Word>
                    ) : null
                }
                action={
                    <QuietLink to="/submissions" tone="mute">
                        City Filing
                    </QuietLink>
                }
            />
            {cheat && (
                <LedgerRow
                    what="Online reporting cheat sheet"
                    sub={`Made ${formatDateTime(cheat.created_at)}`}
                    state="Ready"
                    action={
                        <Quiet tone="mute" onClick={() => setPreview(cheat)}>
                            Open
                        </Quiet>
                    }
                />
            )}
            <LedgerRow
                testId="calm-city-method"
                what="How they file"
                sub={
                    <>
                        {method}
                        {legacyCode && (
                            <>
                                {" · "}Legacy ST code <span className="font-mono">{legacyCode}</span> (not the portal
                                account)
                            </>
                        )}
                    </>
                }
            />
            <LedgerRow
                testId="calm-city-permit"
                what="Permit numbers"
                state={
                    !permit ? null : !permit.applies ? (
                        <Word tone="todo">None needed</Word>
                    ) : permit.unanswered.length > 0 ? (
                        <Word tone="warn">{permit.unanswered.length} to answer</Word>
                    ) : (
                        <Word tone="pass">All answered</Word>
                    )
                }
                action={
                    <Quiet tone={permit?.applies ? "link" : "mute"} onClick={onOpenPermit}>
                        {permit?.applies && permit.unanswered.length > 0 ? "Answer" : "Open"}
                    </Quiet>
                }
            />
            {regQ.data?.city && (
                <LedgerRow
                    testId="calm-city-registration"
                    what="Tester registration"
                    sub={regWarnings.length > 0 ? regWarnings.map((w) => w.text).join(" ") : undefined}
                    state={
                        regWarnings.length > 0 ? (
                            // Warn-only (never holds the job): a heads up, not a blocker.
                            <Word tone="warn">Heads up · {regWarnings.length} to check</Word>
                        ) : (
                            <Word tone="pass">Nothing missing</Word>
                        )
                    }
                    action={
                        regQ.data.providerId ? (
                            <QuietLink to={`/compliance/packets?open=${regQ.data.providerId}`} tone="mute">
                                City packet
                            </QuietLink>
                        ) : undefined
                    }
                />
            )}
            {job.water_provider_is_portal && (
                <div className="pb-1">
                    <ReportInputFlag jobId={job.id} isPortal={job.water_provider_is_portal} quiet />
                </div>
            )}
            <AttachmentPreviewSheet attachment={preview} onClose={() => setPreview(null)} />
            {editor}
        </Section>
    );
}

// ── Customer ────────────────────────────────────────────────────────────────

export function CustomerSection({
    job,
    finishLine,
    sendHistory,
    onResend,
}: {
    job: JobDetail;
    finishLine: FinishLine | null;
    sendHistory: SendHistory | null;
    /** Undefined = not offered (no office role, or nothing ever went out). */
    onResend?: () => void;
}) {
    const [viewEmailId, setViewEmailId] = useState<string | null>(null);
    const sends = sendHistory?.sends ?? [];
    const released = promise(finishLine, "packet_released");
    if (sends.length === 0) {
        const words =
            released && released.state !== "not_applicable"
                ? `packet not sent yet${released.state === "later" ? " (after the visit)" : ` · ${released.detail.replace(/\.$/, "")}`}`
                : "nothing sent yet";
        return <OneLine id="calm-customer" title="Customer" words={words} />;
    }
    return (
        <Section
            id="calm-customer"
            title="Customer"
            summary={`${job.company_name ?? "Customer"} · ${sends.length} send${sends.length === 1 ? "" : "s"}`}
            actions={onResend ? <Quiet onClick={onResend}>Resend packet</Quiet> : undefined}
        >
            {sends.map((s) => {
                const withBody = s.recipients.find((r) => r.hasBody && r.ledgerId);
                return (
                    <div key={s.id} data-testid="calm-send">
                        <LedgerRow
                            what={s.subject || "Customer packet"}
                            sub={`${formatDateTime(s.sentAt)}${s.sentByName ? ` · sent by ${s.sentByName}` : ""}`}
                            state={`${s.recipients.length} recipient${s.recipients.length === 1 ? "" : "s"}`}
                            action={
                                withBody?.ledgerId ? (
                                    <Quiet tone="mute" onClick={() => setViewEmailId(withBody.ledgerId!)}>
                                        View the email
                                    </Quiet>
                                ) : undefined
                            }
                        />
                        {s.recipients.map((r) => {
                            const w = deliveryWord(r);
                            return (
                                <LedgerRow
                                    key={`${r.ledgerId ?? "x"}:${r.email}`}
                                    nested
                                    what={
                                        <>
                                            {r.email}
                                            {r.documents.length > 0 && (
                                                <span className="text-muted-foreground">
                                                    {" "}
                                                    · {r.documents.join(", ")}
                                                </span>
                                            )}
                                            {w.bad && r.reason && (
                                                <span className="block text-[13px] text-red-700 dark:text-red-400">
                                                    {r.reason}
                                                </span>
                                            )}
                                        </>
                                    }
                                    state={
                                        <Word tone={w.bad ? "fail" : w.word === "sent" ? "todo" : "pass"}>
                                            {w.word}
                                        </Word>
                                    }
                                />
                            );
                        })}
                    </div>
                );
            })}
            <SentEmailDialog
                open={!!viewEmailId}
                onClose={() => setViewEmailId(null)}
                jobId={job.id}
                ledgerId={viewEmailId}
            />
        </Section>
    );
}

// ── Vendors ─────────────────────────────────────────────────────────────────

interface JobPoLite {
    id: string;
    po_number: string | null;
    status: string;
    total_amount: string | number | null;
    vendor_name: string | null;
    line_count: number;
    expected_delivery_date: string | null;
    confirmed_delivery_date: string | null;
}

const PO_WORD: Record<string, { word: string; tone: keyof typeof TONE_INK }> = {
    draft: { word: "Draft", tone: "todo" },
    needs_review: { word: "Needs review", tone: "fail" },
    rfq_sent: { word: "RFQ sent", tone: "todo" },
    quoted: { word: "Quoted", tone: "todo" },
    variance_review: { word: "Variance review", tone: "fail" },
    ready_to_finalize: { word: "Ready to finalize", tone: "warn" },
    final_review: { word: "Final review", tone: "warn" },
    final_sent: { word: "Ordered", tone: "todo" },
    confirmed: { word: "Confirmed", tone: "todo" },
    receiving: { word: "Receiving", tone: "todo" },
    closed: { word: "✓ Received", tone: "pass" },
    cancelled: { word: "Cancelled", tone: "todo" },
};

const ymd = (s: string | null) => {
    const m = s ? /^(\d{4})-(\d{2})-(\d{2})/.exec(s) : null;
    return m ? `${Number(m[2])}/${Number(m[3])}` : null;
};

export function VendorsSection({
    jobId,
    isOffice,
    hasSourceEstimate,
    onManage,
}: {
    jobId: string;
    isOffice: boolean;
    hasSourceEstimate: boolean;
    /** Opens the existing PO + Parts cards in a drawer. */
    onManage: () => void;
}) {
    // Same reads (and keys) as the PO and Parts cards.
    const poQ = useQuery({
        queryKey: ["job-purchase-orders", jobId],
        queryFn: () => api.get<{ orders: JobPoLite[] }>(`/api/purchase-orders/for-job/${jobId}`),
        staleTime: 30_000,
        enabled: isOffice,
    });
    const partsQ = useQuery({
        queryKey: ["job-parts", jobId],
        queryFn: () =>
            api.get<{ parts: Array<{ custody_state: string }>; pull_sheet_lines?: Array<{ quantity: number }> }>(
                `/api/tracked-parts/for-job/${jobId}`,
            ),
        staleTime: 30_000,
    });
    const orders = poQ.data?.orders ?? [];
    const parts = partsQ.data?.parts ?? [];
    const pullUnits = (partsQ.data?.pull_sheet_lines ?? []).reduce(
        (n, l) => n + Math.max(1, Math.round(l.quantity)),
        0,
    );
    const notStaged = parts.filter((p) => p.custody_state === "received").length;
    const totalParts = parts.length + pullUnits;

    if (orders.length === 0 && totalParts === 0) {
        return (
            <OneLine
                id="calm-vendors"
                title="Parts & PO"
                words="none ordered"
                actions={
                    isOffice ? (
                        <Quiet tone="mute" onClick={onManage}>
                            {hasSourceEstimate ? "Create purchase order" : "Link a part"}
                        </Quiet>
                    ) : undefined
                }
            />
        );
    }
    return (
        <Section
            id="calm-vendors"
            title="Vendors"
            summary={`${orders.length} PO${orders.length === 1 ? "" : "s"}${totalParts > 0 ? ` · ${totalParts} part${totalParts === 1 ? "" : "s"}` : ""}`}
            actions={
                <Quiet tone="mute" onClick={onManage}>
                    Manage parts
                </Quiet>
            }
        >
            {orders.map((po) => {
                const w = PO_WORD[po.status] ?? { word: po.status, tone: "todo" as const };
                const eta = ymd(po.confirmed_delivery_date) ?? ymd(po.expected_delivery_date);
                return (
                    <LedgerRow
                        key={po.id}
                        what={
                            <>
                                <span className="font-mono">{po.po_number ?? "PO (draft)"}</span>
                                {po.vendor_name && ` · ${po.vendor_name}`}
                            </>
                        }
                        sub={`${po.line_count} line${po.line_count === 1 ? "" : "s"}${eta ? ` · ${po.confirmed_delivery_date ? "arrives" : "eta"} ${eta}` : ""}`}
                        amount={Number(po.total_amount ?? 0) > 0 ? money(po.total_amount) : undefined}
                        state={<Word tone={w.tone}>{w.word}</Word>}
                        action={
                            <QuietLink to={`/purchasing?po=${po.id}`} tone="mute">
                                Open
                            </QuietLink>
                        }
                    />
                );
            })}
            {totalParts > 0 && (
                <LedgerRow
                    what={`Parts for this job · ${totalParts}`}
                    sub={`${parts.length} tracked${pullUnits > 0 ? ` · ${pullUnits} on the pull sheet` : ""}`}
                    state={
                        notStaged > 0 ? (
                            <Word tone="warn">{notStaged} not staged</Word>
                        ) : (
                            <Word tone="pass">Staged</Word>
                        )
                    }
                    action={
                        <Quiet tone="mute" onClick={onManage}>
                            Open
                        </Quiet>
                    }
                />
            )}
        </Section>
    );
}

// ── Money ───────────────────────────────────────────────────────────────────

export function MoneySection({
    job,
    invoices,
    estimates,
    onSendInvoice,
    newInvoiceHref,
    newEstimateHref,
}: {
    job: JobDetail;
    invoices: LinkedInvoice[];
    estimates: LinkedEstimate[];
    onSendInvoice: (invoiceId: string) => void;
    /** Job page v2: the same Create invoice / Create estimate links as the classic header. */
    newInvoiceHref?: string | null;
    newEstimateHref?: string | null;
}) {
    const budgets = useCustomerBudgets(job.customer_id);
    const createLinks =
        newInvoiceHref || newEstimateHref ? (
            <>
                {newInvoiceHref && invoices.length === 0 && <QuietLink to={newInvoiceHref}>+ Invoice</QuietLink>}
                {newEstimateHref && <QuietLink to={newEstimateHref}>+ Estimate</QuietLink>}
            </>
        ) : undefined;
    const budget = chipSummary(budgets, { category: workCategory(job.job_type_name ?? job.title) });
    const tax =
        job.customer_tax_status === "exempt"
            ? "Tax exempt"
            : job.customer_tax_status === "taxable"
              ? "Taxable"
              : job.customer_tax_status === "unknown"
                ? "Tax status not set"
                : null;
    const po = job.customer_po?.trim() ? `PO ${job.customer_po.trim()}` : null;
    const facts = [tax, po].filter(Boolean).join(" · ");

    if (invoices.length === 0 && estimates.length === 0) {
        return (
            <OneLine
                id="calm-money"
                title="Money"
                words={`no invoice or estimate yet${facts ? ` · ${facts}` : ""}`}
                actions={createLinks}
            />
        );
    }
    const totals = deriveMoneyTotals(invoices, estimates);
    return (
        <Section
            id="calm-money"
            title="Money"
            actions={createLinks}
            summary={
                facts || budget ? (
                    <>
                        {facts}
                        {facts && budget ? " · " : ""}
                        {budget && (
                            <span
                                className={
                                    budget.health === "good" ? "" : TONE_INK[budget.health === "over" ? "fail" : "warn"]
                                }
                            >
                                {budget.text}
                            </span>
                        )}
                    </>
                ) : undefined
            }
        >
            {invoices.map((inv) => {
                const w = invoiceWord(inv);
                const paid = Number(inv.payments_total ?? 0);
                const unsent = (inv.emailed_count ?? 0) === 0 && w.word !== "Paid" && w.word !== "Void";
                return (
                    <div key={inv.id}>
                        <LedgerRow
                            testId="calm-invoice"
                            what={`Invoice${inv.invoice_number ? ` #${inv.invoice_number}` : ""}`}
                            sub={inv.undelivered_note ? `Didn't arrive: ${inv.undelivered_note}` : inv.title}
                            amount={money(inv.total_amount)}
                            state={<Word tone={w.tone}>{w.word}</Word>}
                            action={
                                <>
                                    {(unsent || (inv.undelivered_count ?? 0) > 0) && (
                                        <Quiet onClick={() => onSendInvoice(inv.id)}>
                                            {unsent ? "Send" : "Send again"}
                                        </Quiet>
                                    )}
                                    <QuietLink to={`/invoices/${inv.id}`} tone="mute">
                                        Open
                                    </QuietLink>
                                </>
                            }
                        />
                        {paid > 0 && (
                            <LedgerRow
                                nested
                                what={`Payments${inv.last_payment_at ? ` · last ${formatDate(inv.last_payment_at)}` : ""}`}
                                amount={`− ${money(paid)}`}
                            />
                        )}
                    </div>
                );
            })}
            {estimates.map((e) => (
                <LedgerRow
                    key={e.id}
                    what={`Estimate${e.display_number ? ` #${e.display_number}` : ""}`}
                    sub={e.title}
                    amount={money(e.subtotal)}
                    state={
                        <Word
                            tone={
                                e.status === "approved" || e.status === "accepted"
                                    ? "pass"
                                    : e.status === "declined"
                                      ? "fail"
                                      : "todo"
                            }
                        >
                            {e.status.replace(/_/g, " ")}
                        </Word>
                    }
                    action={
                        <QuietLink to={`/estimates/${e.id}`} tone="mute">
                            Open
                        </QuietLink>
                    }
                />
            ))}
            <LedgerRow
                total
                what="Balance"
                amount={money(totals.balance)}
                sub={
                    totals.estimateAwaiting > 0
                        ? `Includes ${money(totals.estimateAwaiting)} in open estimates`
                        : undefined
                }
            />
        </Section>
    );
}

// ── Files ───────────────────────────────────────────────────────────────────

const FILE_CATEGORY: Record<OfficeFile["source"], string> = {
    document: "document",
    closeout_upload: "closeout_upload",
    work_order_uploaded: "work_order_uploaded",
};

export function FilesSection({
    jobId,
    isWorkOrderJob,
    invoices,
    documents,
    closeoutUploads,
    uploadedWorkOrders,
    isUploading,
    onUpload,
    onDeleteDocument,
    deletePending,
    open,
    onToggle,
    onOpenAllPaperwork,
    onOpenPhotos,
}: {
    jobId: string;
    isWorkOrderJob: boolean;
    invoices: LinkedInvoice[];
    documents: ReportAttachment[];
    closeoutUploads: ReportAttachment[];
    uploadedWorkOrders: ReportAttachment[];
    isUploading: boolean;
    onUpload: (files: FileList | null) => void;
    onDeleteDocument: (attachmentId: string) => void;
    deletePending: boolean;
    open: boolean;
    onToggle: () => void;
    /** The full Paperwork card (reports, regenerate, zip, images) in a drawer. */
    onOpenAllPaperwork: () => void;
    onOpenPhotos: () => void;
}) {
    const { can } = useAuth();
    const qc = useQueryClient();
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [preview, setPreview] = useState<ReportAttachment | null>(null);
    const [renaming, setRenaming] = useState<OfficeFile | null>(null);
    const packetQ = usePacketStatus(jobId, can(PERMISSIONS.officeReview.view));
    const useAsWo = useUseAsWorkOrder({ jobId, packet: packetQ.data, invoices });
    const files = officeFiles({ documents, closeoutUploads, uploadedWorkOrders });
    const photosQ = useQuery({
        queryKey: ["job-photos", jobId],
        queryFn: () => api.get<{ attachments: unknown[] }>(`/api/attachments?jobId=${jobId}&category=photo`),
    });
    const jobPhotos = photosQ.data?.attachments.length ?? 0;

    // Same PATCH and words as the Paperwork card's rename (#1538).
    const renameFile = useMutation({
        mutationFn: ({ id, ...body }: { id: string; fileName?: string; photoTag?: string | null }) =>
            api.patch(`/api/attachments/${id}`, body),
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            setRenaming(null);
            toast.success(vars.fileName ? "File renamed — the job log has both names." : "Label saved.");
        },
        onError: (err) => {
            const info = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
            toast.error(info?.message ?? "Couldn't rename the file.");
        },
    });

    const panelId = "calm-files-list";
    return (
        <Section
            id="calm-files"
            title="Files"
            summary={`${files.length} from the office`}
            actions={
                <>
                    <Quiet
                        tone="mute"
                        onClick={onToggle}
                        aria-expanded={open}
                        aria-controls={panelId}
                        data-testid="calm-files-toggle"
                    >
                        {open ? "Hide ▴" : "Show ▸"}
                    </Quiet>
                    {jobPhotos > 0 && (
                        <Quiet tone="mute" onClick={onOpenPhotos} data-testid="calm-photos-link">
                            {jobPhotos} job photo{jobPhotos === 1 ? "" : "s"}
                        </Quiet>
                    )}
                    <Quiet tone="mute" onClick={() => fileRef.current?.click()} disabled={isUploading}>
                        {isUploading ? "Uploading…" : "Upload"}
                    </Quiet>
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            onUpload(e.target.files);
                            e.target.value = "";
                        }}
                    />
                </>
            }
        >
            {open && (
                <div id={panelId} data-testid={panelId}>
                    {files.length === 0 && (
                        <p className="m-0 py-2 text-[14px] text-muted-foreground">Nothing uploaded by the office.</p>
                    )}
                    {files.map((f) => {
                        const canUseAsWo =
                            isWorkOrderJob &&
                            f.source !== "work_order_uploaded" &&
                            !(f.notes ?? "").startsWith("Original work order") &&
                            can(PERMISSIONS.officeReview.manage);
                        const download = () =>
                            downloadAttachmentFile(f.id, f.file_name).catch(() => toast.error("Download failed"));
                        const askDelete = async () => {
                            if (
                                await confirmDialog({
                                    message: "Delete this document?",
                                    destructive: true,
                                    confirmLabel: "Delete",
                                })
                            )
                                onDeleteDocument(f.id);
                        };
                        // Job page v2 (slice 3): the row's menu, from the job-actions list.
                        const rowActions = fileRowActions(
                            {
                                canMakeOfficial: false,
                                canRename: isRenameableCategory(FILE_CATEGORY[f.source]),
                                canUseAsWorkOrder: canUseAsWo,
                                isReport: false,
                                isDocument: f.source === "document",
                            },
                            {
                                "preview-file": () => setPreview(f),
                                "rename-file": () => setRenaming(f),
                                "download-file": () => void download(),
                                "use-as-completed-wo": () => void useAsWo.start(f),
                                "delete-document": () => void askDelete(),
                            },
                        );
                        return (
                            <RowContextMenu key={f.id} actions={rowActions} testId="calm-file-context-menu">
                                <div className="group/row relative border-t first:border-t-0">
                                    <LedgerRow
                                        testId="calm-file"
                                        what={
                                            <Quiet
                                                tone="mute"
                                                className="text-left text-[14.5px] font-medium text-foreground"
                                                onClick={() => setPreview(f)}
                                            >
                                                {f.photo_tag || f.file_name}
                                            </Quiet>
                                        }
                                        sub={provenanceLine({
                                            category: FILE_CATEGORY[f.source],
                                            uploaded_by_name: f.uploaded_by_name,
                                            created_at: f.created_at,
                                            file_size: f.file_size,
                                        })}
                                        action={
                                            <>
                                                <Quiet
                                                    tone="mute"
                                                    onClick={() =>
                                                        downloadAttachmentFile(f.id, f.file_name).catch(() =>
                                                            toast.error("Download failed"),
                                                        )
                                                    }
                                                >
                                                    Download
                                                </Quiet>
                                                {isRenameableCategory(FILE_CATEGORY[f.source]) && (
                                                    <Quiet tone="mute" onClick={() => setRenaming(f)}>
                                                        Rename
                                                    </Quiet>
                                                )}
                                                {isWorkOrderJob &&
                                                    f.source !== "work_order_uploaded" &&
                                                    !(f.notes ?? "").startsWith("Original work order") &&
                                                    can(PERMISSIONS.officeReview.manage) && (
                                                        <Quiet
                                                            tone="mute"
                                                            disabled={useAsWo.pending}
                                                            onClick={() => void useAsWo.start(f)}
                                                        >
                                                            Use as WO
                                                        </Quiet>
                                                    )}
                                                {f.source === "document" && (
                                                    <Quiet
                                                        tone="danger"
                                                        disabled={deletePending}
                                                        onClick={async () => {
                                                            if (
                                                                await confirmDialog({
                                                                    message: "Delete this document?",
                                                                    destructive: true,
                                                                    confirmLabel: "Delete",
                                                                })
                                                            )
                                                                onDeleteDocument(f.id);
                                                        }}
                                                    >
                                                        Delete
                                                    </Quiet>
                                                )}
                                            </>
                                        }
                                    />
                                    <RowTools label={`Actions for ${f.file_name}`}>
                                        <RowIconButton
                                            title="Preview"
                                            onClick={() => setPreview(f)}
                                            testId="file-preview"
                                        >
                                            <Eye className="h-3.5 w-3.5" aria-hidden />
                                        </RowIconButton>
                                        <RowIconButton
                                            title="Download"
                                            onClick={() => void download()}
                                            testId="file-download"
                                        >
                                            <Download className="h-3.5 w-3.5" aria-hidden />
                                        </RowIconButton>
                                        {f.source === "document" && (
                                            <RowIconButton
                                                title="Delete document"
                                                danger
                                                disabled={deletePending}
                                                onClick={() => void askDelete()}
                                                testId="file-delete"
                                            >
                                                <X className="h-3.5 w-3.5" aria-hidden />
                                            </RowIconButton>
                                        )}
                                        <RowMoreMenu
                                            actions={rowActions}
                                            label={`More for ${f.file_name}`}
                                            testId="file-more"
                                        />
                                    </RowTools>
                                </div>
                            </RowContextMenu>
                        );
                    })}
                    <p className="m-0 flex flex-wrap gap-x-4 py-2 text-[14px] text-muted-foreground">
                        <span>
                            Reports and the cheat sheet are under City filing; tech photos are on their devices.
                        </span>
                        <Quiet tone="link" onClick={onOpenAllPaperwork}>
                            All paperwork
                        </Quiet>
                    </p>
                </div>
            )}
            <AttachmentPreviewSheet attachment={preview} onClose={() => setPreview(null)} />
            <RenameFileDialog
                open={renaming !== null}
                onClose={() => setRenaming(null)}
                fileName={renaming?.file_name ?? ""}
                photoTag={renaming?.photo_tag ?? null}
                provenance={
                    renaming
                        ? provenanceLine({
                              category: FILE_CATEGORY[renaming.source],
                              uploaded_by_name: renaming.uploaded_by_name,
                              created_at: renaming.created_at,
                              file_size: renaming.file_size,
                          })
                        : null
                }
                saving={renameFile.isPending}
                onSave={(next) => renaming && renameFile.mutate({ id: renaming.id, ...next })}
            />
        </Section>
    );
}
